// Live coaching (COACH-01..04): the agent's notice and the supervisor's Listen / Whisper / Stop states, from the server's
// call object, capability and refusal codes only. The coach's identity is never shown to the agent (the API omits it).
import type { Call, Capability } from '@bcio-voice/contracts';

export type CoachMode = 'listen' | 'whisper';

/** The agent's plain notice while a coach is connected; null when nobody coaches the call. */
export function agentCoachingNotice(call: Pick<Call, 'state' | 'coaching'>): { mode: CoachMode; text: string } | null {
  const c = call.coaching;
  if (!c || !c.present || (call.state !== 'active' && call.state !== 'held')) return null;
  return c.mode === 'whisper' ? { mode: 'whisper', text: 'A coach is whispering to you. The other party cannot hear the coach.' } : { mode: 'listen', text: 'A coach is listening to this call.' };
}

export type CoachControlInput = {
  capability: Capability | undefined;
  /** The signed-in user holds coaching:live (capability.allowed). */
  userId: string;
  call: Pick<Call, 'state' | 'participants' | 'coaching'>;
  /** This device has a current verified calling registration. */
  deviceReady: boolean;
  /** A coaching command from this user is in flight. */
  pending: boolean;
};
export type CoachButton = { enabled: boolean; pressed: boolean };
export type CoachControls = { listen: CoachButton; whisper: CoachButton; stop: CoachButton; reason: string | null; mine: boolean };

const off: CoachButton = { enabled: false, pressed: false };
/** Honest states: each disabled control comes with the one sentence that explains why. */
export function coachControls(input: CoachControlInput): CoachControls {
  const { capability, call, userId } = input;
  const c = call.coaching;
  const mine = !!c && c.coach_user_id === userId;
  const blocked = (reason: string): CoachControls => ({ listen: off, whisper: off, stop: mine ? { enabled: !input.pending, pressed: false } : off, reason, mine });
  if (!capability || capability.status !== 'enabled') return blocked(capability?.detail ?? 'Live coaching is not available on this phone system.');
  if (!capability.allowed) return blocked('Your role does not include live coaching.');
  // The coach's own supervisor entry (listed only in their view of a call they coach) is not being on the call.
  if (call.participants.some((p) => p.user_id === userId && p.kind !== 'supervisor')) return blocked('You cannot coach a call you are on.');
  if (call.state !== 'active') return blocked(call.state === 'held' ? 'The call is on hold. Coaching is available once it resumes.' : 'Only a connected call can be coached.');
  if (c && !mine) return blocked('Another coach is already on this call.');
  if (!mine && !input.deviceReady) return blocked('Register this device for calling before you coach. Your phone rings to join the call audio.');
  if (input.pending) return { listen: off, whisper: off, stop: mine ? { enabled: false, pressed: false } : off, reason: 'Waiting for the phone system…', mine };
  const mode = mine ? c!.mode : null;
  return {
    listen: { enabled: mode !== 'listen', pressed: mode === 'listen' },
    whisper: { enabled: mode !== 'whisper', pressed: mode === 'whisper' },
    stop: { enabled: mine, pressed: false },
    reason: mine && c!.state === 'dialing' ? 'Your phone is ringing. Answer it to hear the call.' : null,
    mine,
  };
}

/** Sentences for the API's fixed refusal codes. */
export const COACH_REFUSAL_TEXT: Readonly<Record<string, string>> = Object.freeze({
  coach_already_present: 'Another coach is already on this call.',
  coach_busy_elsewhere: 'You are already on another call or coaching session.',
  coach_device_unavailable: 'Your device is not registered for calling. Reconnect it and try again.',
  call_not_coachable: 'This call cannot be coached right now (it is not connected, is on hold, or its type is excluded).',
  coach_self: 'You cannot coach a call you are on.',
  coach_not_present: 'You are not coaching this call.',
  coaching_disabled: 'Live coaching is not enabled on this phone system.',
  permission_denied: 'Your role does not include live coaching.',
});
